import React from "react";
import { useTranslation } from "react-i18next";

import { IOrderDetail, ITaxInfo } from "../../apis/useGetOrderDetail/types";
import { displayCentsWithDollarSign } from "../../common/helpers/stringUtils";
import Condition from "../../components/Conditional/Condition";
import "./OrderConfirmation.scss";

interface OrderLayoutDefaultProps {
	orderDetail: IOrderDetail;
	onStartOver: () => void;
}

const SERVICE_CHARGE_REGEX = /service\s*charge/i;
const GST_REGEX = /gst/i;

const findTaxEntry = (tax: ITaxInfo[] | undefined, regex: RegExp): ITaxInfo | undefined =>
	tax?.find((entry) => regex.test(entry.name ?? "") || regex.test(entry.type ?? ""));

const toRatePercent = (rate: number | undefined): number => (rate == null ? 0 : Math.round(rate));

const OrderLayoutDefault: React.FC<OrderLayoutDefaultProps> = ({ orderDetail, onStartOver }) => {
	const { t } = useTranslation();
	const { order_items, created_at, order_reference } = orderDetail;
	const { item_info, tax } = order_items;
	const { subtotal_value, total_discount, net_value, bcrs_total } = order_items.orders_figures;
	const bcrsDepositAmount = bcrs_total ?? 0;
	const serviceChargeTax = findTaxEntry(tax, SERVICE_CHARGE_REGEX);
	const gstTax = findTaxEntry(tax, GST_REGEX);

	return (
		<div className="OrderConfirmationPage">
			<div className="OrderConfirmationHeader">
				<div className="SuccessSection">
					<div className="SuccessIcon">✓</div>
					<h1 className="SuccessTitle">{t("orderConfirmation.successTitle")}</h1>
					<p className="SuccessMessage">{t("orderConfirmation.successMessage")}</p>
				</div>

				<div className="OrderReferenceSection">
					<span className="Label">{t("orderConfirmation.orderReference")}</span>
					<span className="Value">{order_reference}</span>
				</div>
			</div>
			<div className="OrderConfirmationContent">
				<div className="OrderDetailSection">
					<h2 className="SectionTitle">{t("orderConfirmation.orderDetails")}</h2>

					<div className="OrderInfo">
						{created_at && (
							<div className="OrderInfoRow">
								<span className="Label">{t("orderConfirmation.orderTime")}</span>
								<span className="Value">{new Date(created_at).toLocaleString()}</span>
							</div>
						)}
					</div>

					<div className="OrderSummary">
						<h3 className="SubSectionTitle">{t("orderConfirmation.orderSummary")}</h3>

						<div className="OrderItems">
							{item_info.map((item, index) => (
								<div key={`${item.food_supplier_menu_item_id}-${index}`} className="OrderItem">
									<div className="ItemInfo">
										<span className="ItemName">{item.title_en}</span>
										<span className="ItemQuantity">x{item.quantity}</span>
									</div>
									<span className="ItemPrice">
										{displayCentsWithDollarSign(item.subtotal_value)}
									</span>
								</div>
							))}
						</div>

						<div className="OrderTotals">
							<div className="TotalLine">
								<span>{t("orderConfirmation.subtotal")}</span>
								<span>{displayCentsWithDollarSign(subtotal_value - bcrsDepositAmount)}</span>
							</div>
							{total_discount > 0 && (
								<div className="TotalLine">
									<span>{t("orderConfirmation.discount")}</span>
									<span>-{displayCentsWithDollarSign(total_discount)}</span>
								</div>
							)}
							<Condition check={!!serviceChargeTax}>
								<div className="TotalLine">
									<span>
										{t("paymentPage.serviceCharge", {
											rate: toRatePercent(serviceChargeTax?.rate),
										})}
									</span>
									<span>{displayCentsWithDollarSign(serviceChargeTax?.amount ?? 0)}</span>
								</div>
							</Condition>
							<Condition check={!!gstTax}>
								<div className="TotalLine">
									<span>{t("paymentPage.gst")}</span>
									<span>{displayCentsWithDollarSign(gstTax?.amount ?? 0)}</span>
								</div>
							</Condition>
							<Condition check={bcrsDepositAmount > 0}>
								<div className="TotalLine BcrsRow">
									<span>{t("paymentPage.bcrsDeposit")}</span>
									<span>{displayCentsWithDollarSign(bcrsDepositAmount)}</span>
								</div>
								<div className="BcrsDisclaimer">{t("cartPage.bcrs.disclaimer")}</div>
							</Condition>
							<div className="TotalLine FinalTotal">
								<span>{t("orderConfirmation.total")}</span>
								<span>{displayCentsWithDollarSign(net_value)}</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="ActionsSection">
				<button className="StartOverButton" onClick={onStartOver}>
					{t("common.startOver")}
				</button>
			</div>
		</div>
	);
};

export default OrderLayoutDefault;
